import type { App } from 'vue';
import type { NavigationGuard, Router, RouterHistory } from 'vue-router';
import { createMemoryHistory, createRouter, createWebHistory } from 'vue-router';
import multiguard from 'vue-router-multiguard';
import routes from '~/pages';

import useRouteManagerStore from '~/stores/routeManager';

import * as middlewares from '../middleware/index';
import CookieService from './CookieService';
import EventLoggerService from './EventLoggerService';
import { isClient, isSSRHydration, isServer } from '~/utils/EnvUtils';

import { routerUtils } from 'utilities';

const routerHistory: RouterHistory = isServer() ? createMemoryHistory() : createWebHistory();

export function createInternalRouter(app: App<Element>) {
  routes.forEach((route, routeIndex) => {
    const typedMiddleware: { [index: string]: any } = middlewares;
    const defaultMiddlewares: NavigationGuard[] = [];
    const middlewaresToAdd: NavigationGuard[] = [];

    if (route.meta?.middleware) {
      const namesOfMiddlewares: any = route.meta.middleware;
      middlewaresToAdd.push(
        ...namesOfMiddlewares
          .split(',')
          .map((name: string) => typedMiddleware[name.trim()]),
      );

      defaultMiddlewares.push(middlewares.msTeamsSessionHandler);
    }
    // Mention all the default middleware here.
    defaultMiddlewares.push(middlewares.profileStatus);

    routes[routeIndex].beforeEnter = multiguard([
      ...defaultMiddlewares,
      ...middlewaresToAdd,
      routes[routeIndex].beforeEnter as NavigationGuard,
    ].filter(Boolean));
  });

  // injects testing routes or POC routes
  if (!isServer()) {
    routes.push({
      path: '/admin/internal/question/:questionId',
      beforeEnter: multiguard([middlewares.checkInternal]),
      component: () => import('~/internal/pages/question.vue'),
    });
  }

  const router = createRouter({
    history: routerHistory,
    routes,
    scrollBehavior() {
      const appBodyContainer = document?.getElementById?.('app-body-container');
      if (appBodyContainer) {
        appBodyContainer.scrollTop = 0;
      }
      return false;
    },
  });

  if (!isServer()) {
    router.beforeEach(async (to, from) => {
      if (to.path.includes('/forwork/plans')) {
        EventLoggerService.logEvent('qfw_pricing_page_intent', { to: to.fullPath, from: from.fullPath });
      }
      // only update feature flag when page changes, ignore for SSR hydration as store already has the flags available
      if (to.name !== from.name && !isSSRHydration()) {
        await app.config.globalProperties.$featureFlags.updateFeatureFlags();
        app.config.globalProperties.$featureFlags.setUserAttributes({
          ...app.config.globalProperties.$user.userStore,
          isSuper: app.config.globalProperties.$user.isSuperOrHasNonQfwSubscription || false,
          requestCountry: app.config.globalProperties?.store?.state?.serverData?.requestCountry || 'US',
          quizizz_uid: CookieService.get('quizizz_uid'),
          slot: CookieService.get('QUIZIZZ_EXP_SLOT') || '0',
          experiment: CookieService.get('QUIZIZZ_EXP_NAME') || 'main',
        });
      }

      // open new editor if old editor is opened
      if (to.path.includes('/quiz/creator')) {
        router.replace(`/admin/quiz/${to.params.id}/edit`);
      }

      // adding the property $appData rather than $data because $data is modified by each component

      app.config.globalProperties.$appData = {
        ...(app.config.globalProperties?.$appData || {}),
        ...(to.params || {}),
      };
    });

    router.afterEach((to, from) => {
      if (!isClient()) {
        return;
      }

      if (to.path === from.path) {
        return;
      }

      EventLoggerService.processPageViewEvents(to, from, to.query, useRouteManagerStore().getSourceName);
    });
  }

  router.onError(routerUtils.routerErrorHandler);

  return router;
}

let routerInstance: Router | null = null;

export default function getRouter(app: App<Element> | null = null): Router {
  if (!routerInstance) {
    if (!app) {
      throw new Error('App is needed to create a Router, its created in main.ts during app initialization');
    }
    const router: Router = app._context.provides.qRouter ?? createInternalRouter(app!);
    if (isServer()) {
      app._context.provides.qRouter = router;
      return router;
    } else {
      routerInstance = router;
    }
  }

  return routerInstance;
}
